import React from 'react';
import { useTranslation } from '../../contexts/LanguageContext';
import { useToast } from '../Shared/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';

const ENV_STORAGE_PREFIX = 'toolEnv_';

function dotClass(allowed, liveStatus) {
  if (!allowed) return 'dot-locked';
  if (liveStatus === 'live') return 'dot-live';
  if (liveStatus === 'degraded' || liveStatus === 'timeout') return 'dot-degraded';
  if (liveStatus === 'down') return 'dot-down';
  return 'dot-active';
}

function statusTitle(t, allowed, liveStatus) {
  if (!allowed) return t('dashboard.status.notLicensed');
  if (liveStatus === 'live') return t('common.status.live');
  if (liveStatus === 'degraded') return t('common.status.degraded');
  if (liveStatus === 'timeout') return t('common.status.timeout');
  if (liveStatus === 'down') return t('common.status.down');
  return t('common.status.unknown');
}

function modClass(env) {
  if (env === 'prod') return 'mod-prod';
  // 'quality' bleibt als Alias fuer evtl. noch nicht migrierte Altdaten gemappt.
  if (env === 'staging' || env === 'quality') return 'mod-staging';
  if (env === 'demo') return 'mod-demo';
  return 'mod-dev';
}

export default function ToolCard({ tool, liveStatus, selectedEnv, onEnvChange }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { userRole } = useAuth();
  const { api } = useApi();

  const allowed = tool.allowed !== false;
  const isSoon = !tool.base_url && !allowed;
  const environments = tool.environments || [];
  const env = selectedEnv || environments[0] || 'prod';

  let statusBadge = 'badge-muted';
  let statusText = tool.reason || t('dashboard.status.notLicensed');
  let actionText = t('dashboard.action.unavailable');

  if (allowed) {
    statusBadge = 'badge-success';
    statusText = tool.role ? String(tool.role).toUpperCase() : t('dashboard.status.active');
    actionText = t('dashboard.action.open');
  } else if (isSoon) {
    statusBadge = 'badge-warning';
    statusText = t('dashboard.status.comingSoon');
    actionText = t('dashboard.comingSoon');
  }

  const dot = dotClass(allowed, liveStatus);
  const dotTitle = statusTitle(t, allowed, liveStatus);

  const handleEnvClick = (e, m) => {
    e.stopPropagation();
    if (onEnvChange) onEnvChange(tool.tool_id, m);
  };

  const handleClick = async () => {
    if (!allowed) {
      showToast(t('dashboard.notLicensed'), 'error');
      return;
    }
    const envUrls = (tool.metadata && tool.metadata.urls) || {};
    const resolvedUrl = envUrls[env] || tool.base_url || `https://${tool.tool_id}.secaas.it`;
    if (!resolvedUrl) {
      showToast(t('dashboard.urlMissing'), 'error');
      return;
    }
    try {
      const data = await api(
        `auth/token/exchange?tool_id=${encodeURIComponent(tool.tool_id)}&env=${encodeURIComponent(env)}`,
        { method: 'POST', body: {} }
      );
      let url = resolvedUrl;
      if (data?.tool_token) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}platform_token=${encodeURIComponent(data.tool_token)}`;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Token-Exchange-Fehler → trotzdem oeffnen
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={'tool-card' + (allowed ? '' : ' locked')}
      style={{ '--card-accent': tool.color || 'var(--accent)' }}
      onClick={handleClick}
      role={allowed ? 'button' : 'presentation'}
      tabIndex={allowed ? 0 : -1}
    >
      <div className="tool-card-top">
        <div className="tool-icon" style={{ background: `${tool.color || 'var(--accent)'}22` }}>
          {tool.icon || '◈'}
        </div>
        <div className={`tool-dot ${dot}`} title={dotTitle} />
      </div>

      <div className="tool-name">{tool.label || tool.tool_id}</div>
      <div className="tool-desc">{tool.description || ''}</div>

      <div className="tool-tags">
        {environments.map((m) => {
          const active = m === env;
          const cls = modClass(m);
          return (
            <span
              key={m}
              className={`mod-pill ${cls}`}
              onClick={(e) => handleEnvClick(e, m)}
              style={{
                cursor: 'pointer',
                outline: active ? '2px solid currentColor' : 'none',
                outlineOffset: active ? 1 : 0,
                fontWeight: active ? 700 : 600,
              }}
            >
              {m.toUpperCase()}
            </span>
          );
        })}
        {userRole === 'platform_admin' && tool.visibility === 'internal' && (
          <span className="badge badge-internal" style={{ fontSize: 10 }}>INTERN</span>
        )}
      </div>

      <div className="tool-footer">
        <span className={`badge ${statusBadge}`}>{statusText}</span>
        <span className="tool-action">{actionText}</span>
      </div>
    </div>
  );
}

export { ENV_STORAGE_PREFIX };
